import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MotorDataChart } from "./components/MotorDataChart";
import { MotorDataGrid } from "./components/MotorDataGrid";
import { useMotorStatusData } from "./hooks/useMotorStatusData";
import { buildChartDataset } from "./models/chartDataset";
import { toMotorDataRows } from "./models/MotorDataRow";
import { MotorTheme } from "./models/motorTheme";
import { summariseDiagnostics } from "./models/summariseDiagnostics";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { useDiagnosticsByIds } from "@/hooks/diagnostic/useDiagnosticsByIds";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { DeviceSelect } from "@/pages/live-data/components/DeviceSelect";
import { LiveDataChecklist } from "@/pages/live-data/components/LiveDataChecklist";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { consumePendingDeviceId } from "@/utilities/navigation/inStructureNavigation";

export default function MotorDataPage() {
  const { t, i18n } = useTranslation();
  const { geotabClient } = useGeotabClient();

  const [selectedDevice, setSelectedDevice] = useState<DeviceWithVehicleInfo | null>(null);
  const [fromDate, setFromDate] = useState(() => startOfDay(new Date()));
  const [toDate, setToDate] = useState(() => endOfDay(new Date()));
  const [chartedIds, setChartedIds] = useState<string[]>([]);

  const preselectedDeviceId = usePreselectedDeviceId();
  const selectedDeviceId = selectedDevice?.id;

  useEffect(() => {
    // Applied once, and never over a vehicle the user has picked themselves.
    if (!preselectedDeviceId || selectedDevice) {
      return;
    }

    let cancelled = false;
    // Deliberately not through createGroupFilter, unlike the list queries: this is a deep link
    // from another page, and the vehicle the reader came from is the one they meant — narrowing it
    // by whatever the header group filter happens to be set to would answer with nothing.
    geotabClient
      .get("Device", { id: preselectedDeviceId }, 1)
      .then(devices => {
        const found = devices?.[0];
        if (!cancelled && found) {
          setSelectedDevice(current => current ?? found);
        }
      })
      .catch(() => {
        // Nothing to do: the vehicle can still be picked by hand.
      });

    return () => {
      cancelled = true;
    };
  }, [geotabClient, preselectedDeviceId, selectedDevice]);

  const { data: statusData, isLoading, isFetching, error } = useMotorStatusData(
    selectedDeviceId,
    fromDate,
    toDate,
  );
  const readings = useMemo(() => statusData ?? [], [statusData]);

  const diagnosticIds = useMemo(
    () => [
      ...new Set(
        readings.map(entry => entry.diagnostic?.id).filter((id): id is string => !!id),
      ),
    ],
    [readings],
  );
  const { data: diagnostics } = useDiagnosticsByIds(diagnosticIds, diagnosticIds.length === 0);

  const summaries = useMemo(
    () => summariseDiagnostics(readings, diagnostics ?? {}),
    [readings, diagnostics],
  );

  const rows = useMemo(
    () =>
      toMotorDataRows(summaries, {
        themeLabel: (theme: MotorTheme) => t(`motorData.theme.${theme}`, theme),
        unknownSource: t("motorData.unknownSource", "Unbekannt"),
        language: i18n.language,
        missing: "---",
      }),
    [summaries, t, i18n.language],
  );

  const chartedSet = useMemo(() => new Set(chartedIds), [chartedIds]);
  const chartedSummaries = useMemo(
    () => summaries.filter(summary => chartedSet.has(summary.diagnosticId)),
    [summaries, chartedSet],
  );
  const chartDataset = useMemo(
    () => buildChartDataset(readings, chartedIds),
    [readings, chartedIds],
  );

  const onChartedChange = useCallback((diagnosticId: string, charted: boolean) => {
    setChartedIds(current => {
      if (charted) {
        return current.includes(diagnosticId) ? current : [...current, diagnosticId];
      }
      return current.filter(id => id !== diagnosticId);
    });
  }, []);

  // The readings of a vehicle are exactly what the live data checklist reads, so the same
  // checklist can say here whether this vehicle is reporting everything it should.
  const checklistRows = useMemo<LiveDataRow[]>(
    () =>
      summaries.map(summary => ({
        id: summary.diagnosticId,
        diagnosticId: summary.diagnosticId,
        diagnosticName: summary.name,
        value: Number(summary.latest),
        unitOfMeasure: summary.unit,
        dateTime: summary.latestAt,
        source: summary.sourceName ?? summary.sourceId ?? "",
      })),
    [summaries],
  );

  return (
    <FilterStackLayout
      filters={
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          sx={{ width: "100%" }}
        >
          <Box sx={{ width: "100%" }}>
            {/* The live data page's picker: one vehicle, searchable by name, plate or VIN, and
                each option says whether that vehicle is moving. */}
            <DeviceSelect
              value={selectedDevice}
              onChange={device => {
                setSelectedDevice(device);
                setChartedIds([]);
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              {t("motorData.searchHint")}
            </Typography>
          </Box>
          <DateRangeSelect
            fromDate={fromDate}
            toDate={toDate}
            onChange={(from, to) => {
              if (from) setFromDate(startOfDay(from));
              if (to) setToDate(endOfDay(to));
            }}
          />
        </Stack>
      }
      content={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 2,
            gap: 1,
          }}
        >
          {error && <Alert severity="error">{error.message}</Alert>}

          <LiveDataChecklist
            device={selectedDevice as DeviceWithVehicleInfo | null}
            rows={checklistRows}
          />

          {!selectedDeviceId ? (
            <Centred>
              <Typography color="text.secondary">
                {t("motorData.selectAsset", "Bitte ein Fahrzeug auswählen")}
              </Typography>
            </Centred>
          ) : isLoading ? (
            <Centred>
              <CircularProgress size={28} />
            </Centred>
          ) : rows.length === 0 ? (
            <Centred>
              <Typography color="text.secondary">
                {t("motorData.noData", "Für diesen Zeitraum liegen keine Messwerte vor")}
              </Typography>
            </Centred>
          ) : (
            <>
              <MotorDataGrid
                rows={rows}
                loading={isFetching && rows.length === 0}
                chartedIds={chartedSet}
                onChartedChange={onChartedChange}
              />
              {chartedSummaries.length > 0 && (
                <MotorDataChart points={chartDataset.points} series={chartedSummaries} />
              )}
            </>
          )}

          {isFetching && rows.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t("motorData.refreshing", "Messwerte werden aktualisiert …")}
            </Typography>
          )}
        </Box>
      }
    />
  );
}

function Centred({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: "grid", placeItems: "center", width: "100%", py: 6 }}>{children}</Box>;
}

/**
 * The vehicle to start on, from the address bar or from a jump off another page.
 *
 * Read once: the handover is for one mount, and re-reading it would fight the user the moment
 * they pick something else.
 */
function usePreselectedDeviceId(): string | null {
  return useMemo(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("deviceId")?.trim();
    return fromUrl && fromUrl.length > 0 ? fromUrl : consumePendingDeviceId("motorData");
  }, []);
}
